/**
 * Custom React Hook for SEO Management
 * Provides easy-to-use SEO functionality for React components
 */

import { useEffect } from 'react';
import {
  setupBlogPostSEO,
  setupBlogListSEO,
  setupCategorySEO,
  setupTagSEO,
  setupSearchSEO,
  cleanupSEO
} from '../utils/seoUtils';

/**
 * Hook for blog post SEO
 */
export const useBlogPostSEO = (blogPost) => {
  useEffect(() => {
    if (blogPost) {
      setupBlogPostSEO(blogPost);
    }

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, [blogPost]);
};

/**
 * Hook for blog list SEO
 */
export const useBlogListSEO = (blogs, pageTitle, pageDescription) => {
  useEffect(() => {
    setupBlogListSEO(blogs, pageTitle, pageDescription);

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, [blogs, pageTitle, pageDescription]);
};

/**
 * Hook for category page SEO
 */
export const useCategorySEO = (category, blogs) => {
  useEffect(() => {
    if (category) {
      setupCategorySEO(category, blogs);
    }

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, [category, blogs]);
};

/**
 * Hook for tag page SEO
 */
export const useTagSEO = (tag, blogs) => {
  useEffect(() => {
    if (tag) {
      setupTagSEO(tag, blogs);
    }

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, [tag, blogs]);
};

/**
 * Hook for search results SEO
 */
export const useSearchSEO = (query, blogs) => {
  useEffect(() => {
    if (query) {
      setupSearchSEO(query, blogs);
    }

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, [query, blogs]);
};

/**
 * Generic SEO hook for custom setups
 */
export const useSEO = (setupFunction, dependencies = []) => {
  useEffect(() => {
    if (setupFunction) {
      setupFunction();
    }

    // Cleanup on unmount
    return () => {
      cleanupSEO();
    };
  }, dependencies);
};

export default {
  useBlogPostSEO,
  useBlogListSEO,
  useCategorySEO,
  useTagSEO,
  useSearchSEO,
  useSEO
};